

const DOUX = matchMedia('(prefers-reduced-motion: reduce)');

class MvtReveler extends HTMLElement {
  connectedCallback() {
    if (this.hasAttribute('monte')) return;
    this.setAttribute('monte', '');
    const sel = this.getAttribute('cible');
    const cibles = sel ? [...this.querySelectorAll(sel)] : [...this.children];
    if (!cibles.length) return;

    if (DOUX.matches || !('IntersectionObserver' in window)) {
      cibles.forEach((c) => c.classList.add('est-visible'));
      return;
    }

    const pas = parseInt(this.getAttribute('cascade') || '80', 10);
    cibles.forEach((c) => c.setAttribute('data-revele', ''));

    this.observateur = new IntersectionObserver((entrees, obs) => {
      entrees.forEach((e) => {
        if (!e.isIntersecting) return;
        const i = cibles.indexOf(e.target);
        e.target.style.setProperty('--retard', `${Math.max(0, i) * pas}ms`);
        e.target.classList.add('est-visible');

        e.target.addEventListener('transitionend', () => e.target.classList.add('est-fini'),
                                  { once: true });
        obs.unobserve(e.target);
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.08 });

    cibles.forEach((c) => this.observateur.observe(c));
  }
  disconnectedCallback() { this.observateur?.disconnect(); }
}

class MvtRecit extends HTMLElement {
  connectedCallback() {
    if (this.hasAttribute('monte')) return;
    this.setAttribute('monte', '');
    const etapes = [...this.querySelectorAll('[data-recit-etape]')];
    const vues = [...this.querySelectorAll('[data-recit-vue]')];
    if (!etapes.length || !vues.length) return;

    if (DOUX.matches || !('IntersectionObserver' in window)) {
      vues.forEach((v) => v.classList.add('est-active'));
      return;
    }
    vues[0].classList.add('est-active');

    this.observateur = new IntersectionObserver((entrees) => {
      entrees.forEach((e) => {
        if (!e.isIntersecting) return;
        const i = etapes.indexOf(e.target);
        vues.forEach((v, j) => v.classList.toggle('est-active', j === i));
      });
    }, { rootMargin: '-45% 0px -45% 0px' });

    etapes.forEach((e) => this.observateur.observe(e));
  }
  disconnectedCallback() { this.observateur?.disconnect(); }
}

class MvtHero extends HTMLElement {
  connectedCallback() {
    if (this.hasAttribute('monte')) return;
    this.setAttribute('monte', '');
    const hero = this.querySelector('.hero') || this;
    if (DOUX.matches) { hero.classList.add('est-prete'); return; }

    const lignes = [...this.querySelectorAll('.hero__ligne>span')];
    const suite = [...this.querySelectorAll('.hero__phrase,.hero__action')];
    hero.setAttribute('data-anime', '');
    lignes.forEach((l, i) => l.style.setProperty('--retard', `${i * 120}ms`));
    suite.forEach((s, i) => s.style.setProperty('--retard', `${lignes.length * 120 + i * 140}ms`));

    requestAnimationFrame(() => requestAnimationFrame(() => hero.classList.add('est-prete')));

    const media = this.querySelector('.hero__media');
    if (!media || !('IntersectionObserver' in window)) return;
    let visible = false, attendu = false;
    const bouge = () => {
      attendu = false;
      const y = Math.min(window.scrollY, innerHeight);
      media.style.transform = `translate3d(0, ${y * 0.15}px, 0)`;
    };
    this.observateur = new IntersectionObserver(([e]) => {
      visible = e.isIntersecting;
      if (!visible) media.style.transform = '';
    });
    this.observateur.observe(this);
    this.surDefilement = () => {
      if (!visible || attendu) return;
      attendu = true; requestAnimationFrame(bouge);
    };
    addEventListener('scroll', this.surDefilement, { passive: true });
  }
  disconnectedCallback() {
    this.observateur?.disconnect();
    if (this.surDefilement) removeEventListener('scroll', this.surDefilement);
  }
}

customElements.define('mvt-reveler', MvtReveler);
customElements.define('mvt-recit', MvtRecit);
customElements.define('mvt-hero', MvtHero);

class MvtHeroVideo extends HTMLElement {
  connectedCallback() {
    if (this.hasAttribute('monte')) return;
    this.setAttribute('monte', '');
    this.video = this.querySelector('video');
    if (!this.video) return;

    const eco = navigator.connection && (navigator.connection.saveData ||
                /2g/.test(navigator.connection.effectiveType || ''));
    if (DOUX.matches || eco) return;

    const partir = () => {

      if (!this.getClientRects().length) return;
      const sources = [...this.video.querySelectorAll('source[data-src]')];
      if (!sources.length) return;
      sources.forEach((s) => { s.src = s.dataset.src; s.removeAttribute('data-src'); });
      this.video.load();
      this.video.play().then(() => this.classList.add('joue')).catch(() => {});
    };
    const auRepos = () => (window.requestIdleCallback
      ? requestIdleCallback(partir, { timeout: 2500 }) : setTimeout(partir, 900));
    if (document.readyState === 'complete') auRepos();
    else addEventListener('load', auRepos, { once: true });

    const pause = this.querySelector('[data-hero-pause]');
    pause?.addEventListener('click', () => {
      const arrete = this.video.paused;
      arrete ? this.video.play() : this.video.pause();
      pause.setAttribute('aria-label', arrete ? 'Mettre la video en pause' : 'Reprendre la video');
      this.classList.toggle('en-pause', !arrete);
    });
  }
}
customElements.define('mvt-hero-video', MvtHeroVideo);

class MvtGalerie extends HTMLElement {
  connectedCallback() {
    if (this.hasAttribute('monte')) return;
    this.setAttribute('monte', '');
    this.piste = this.querySelector('[data-galerie-piste]');
    this.vues = [...this.querySelectorAll('.galerie__vue')];
    const barre = this.parentElement.querySelector('[data-galerie-vignettes]');
    this.vignettes = barre ? [...barre.querySelectorAll('.vignette')] : [];
    if (!this.piste || !this.vues.length) return;
    this.bureau = matchMedia('(min-width:990px)');
    this.courante = 0;

    this.vignettes.forEach((v, i) => {
      v.addEventListener('click', () => this.aller(i));
    });

    this.querySelector('[data-galerie-prec]')?.addEventListener('click', () => this.aller(this.courante - 1));
    this.querySelector('[data-galerie-suiv]')?.addEventListener('click', () => this.aller(this.courante + 1));
    this.addEventListener('keydown', (e) => {
      if (!this.bureau.matches || e.target.closest('video')) return;
      if (e.key === 'ArrowLeft') { e.preventDefault(); this.aller(this.courante - 1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); this.aller(this.courante + 1); }
    });

    const io = new IntersectionObserver((entrees) => {
      entrees.forEach((e) => {
        if (e.isIntersecting) this.marquer(this.vues.indexOf(e.target));
      });
    }, { root: this.piste, threshold: 0.6 });
    this.vues.forEach((v) => io.observe(v));
  }

  aller(i) {
    const cible = this.vues[Math.max(0, Math.min(i, this.vues.length - 1))];
    if (!cible) return;
    this.piste.scrollTo({ left: cible.offsetLeft, behavior: 'smooth' });
    this.marquer(this.vues.indexOf(cible));
  }

  marquer(i) {
    if (i < 0) return;
    this.courante = i;
    this.vignettes.forEach((v, k) =>
      v.setAttribute('aria-current', String(k === i)));
    const prec = this.querySelector('[data-galerie-prec]');
    const suiv = this.querySelector('[data-galerie-suiv]');
    if (prec) prec.disabled = i === 0;
    if (suiv) suiv.disabled = i === this.vues.length - 1;

    const compteur = this.querySelector('[data-galerie-compteur]');
    if (compteur) compteur.textContent = `${i + 1} / ${this.vues.length}`;

    this.vues.forEach((v, k) => {
      const auto = v.querySelector('video[data-auto]');
      if (!auto) return;
      if (k === i) { auto.preload = 'auto'; const p = auto.play(); if (p && p.catch) p.catch(() => {}); }
      else auto.pause();
    });

    const vignTel = this.vignettes[i];
    const rangee = vignTel?.parentElement;
    if (rangee && !this.bureau.matches) {
      const c = rangee.getBoundingClientRect(), r = vignTel.getBoundingClientRect();
      if (r.left < c.left || r.right > c.right) rangee.scrollBy({ left: r.left - c.left - (c.width - r.width) / 2, behavior: 'smooth' });
    }
    if (!this.bureau.matches) return;

    this.vues.forEach((v, k) => { if (k !== i) v.querySelector('video')?.pause(); });

    const vign = this.vignettes[i];
    const col = vign?.parentElement;
    if (col) {
      const c = col.getBoundingClientRect(), r = vign.getBoundingClientRect();
      if (r.top < c.top || r.bottom > c.bottom) col.scrollBy({ top: r.top - c.top - (c.height - r.height) / 2, behavior: 'smooth' });
    }
  }
}
customElements.define('mvt-galerie', MvtGalerie);

class MvtComparer extends HTMLElement {
  connectedCallback() {
    if (this.hasAttribute('monte')) return;
    this.setAttribute('monte', '');
    const curseur = this.querySelector('.comparer__curseur');
    if (!curseur) return;
    this.dataset.actif = '';
    const poser = () => this.style.setProperty('--coupe', `${curseur.value}%`);
    poser();
    curseur.addEventListener('input', poser);

    const cadre = this.querySelector('.comparer__cadre');
    if (!cadre) return;
    const suivre = (e) => {
      const r = cadre.getBoundingClientRect();
      const x = (e.touches ? e.touches[0].clientX : e.clientX) - r.left;
      curseur.value = String(Math.max(0, Math.min(100, (x / r.width) * 100)));
      poser();
    };
    let actif = false;
    const debut = (e) => { actif = true; suivre(e); };
    const fin = () => { actif = false; };
    cadre.addEventListener('pointerdown', debut);
    cadre.addEventListener('pointermove', (e) => { if (actif) suivre(e); });
    addEventListener('pointerup', fin);
    addEventListener('pointercancel', fin);
  }
}
customElements.define('mvt-comparer', MvtComparer);

class MvtEntete extends HTMLElement {
  connectedCallback() {
    if (this.hasAttribute('monte')) return;
    this.setAttribute('monte', '');

    this.env = this.querySelector('[data-menu-enveloppe]');
    this.menu = this.querySelector('[data-menu]');
    this.bouton = this.querySelector('[data-ouvrir-menu]');
    this.p2 = [...this.querySelectorAll('[data-niveau="2"]')];

    this.annonce();
    this.replier();
    this.brancher();
    this.header();
  }

  annonce() {
    const mots = [...this.querySelectorAll('[data-annonce-mot]')];
    if (mots.length < 2) return;
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let i = 0;
    mots.forEach((m, k) => { m.style.opacity = k === 0 ? '1' : '0'; });
    this.horloge = setInterval(() => {
      mots[i].style.opacity = '0';
      i = (i + 1) % mots.length;
      mots[i].style.opacity = '1';
    }, 6000);
  }

  replier() {
    this.p2.forEach((p) => { p.hidden = true; });
  }

  brancher() {

    this.env.addEventListener('toggle', () => {
      if (this.env.open) this.ouvrir(); else this.fermer();
    });
    this.querySelectorAll('[data-fermer-menu]').forEach(
      (b) => b.addEventListener('click', () => this.fermer()));
    this.querySelectorAll('[data-ouvrir-niveau2]').forEach((b) => {
      b.addEventListener('click', () => this.niveau2(b.dataset.ouvrirNiveau2, b));
    });
    this.querySelectorAll('[data-retour-niveau1]').forEach(
      (b) => b.addEventListener('click', () => this.niveau1()));

    this.surTouche = (e) => {
      if (e.key !== 'Escape' || this.menu.hidden) return;
      const ouvert = this.p2.find((p) => !p.hidden);
      if (ouvert) this.niveau1(); else this.fermer();
    };
    addEventListener('keydown', this.surTouche);
  }

  ouvrir() {

    requestAnimationFrame(() => this.menu.classList.add('est-ouvert'));
    document.documentElement.style.overflow = 'hidden';
    const cible = this.menu.querySelector('.menu__croix, .menu__entree');
    if (cible) cible.focus();
  }

  fermer() {
    if (this.env.open) { this.env.open = false; return; }
    this.niveau1();
    this.menu.classList.remove('est-ouvert');
    document.documentElement.style.overflow = '';
    this.bouton.focus();
  }

  niveau2(id, bouton) {
    this.p2.forEach((p) => {
      const pour = p.dataset.pour === id;
      p.hidden = !pour;
      if (pour) requestAnimationFrame(() => p.classList.add('est-ouvert'));
      else p.classList.remove('est-ouvert');
    });
    this.querySelectorAll('[data-ouvrir-niveau2]').forEach(
      (b) => b.setAttribute('aria-expanded', String(b === bouton)));
    const p = this.p2.find((x) => x.dataset.pour === id);
    const r = p && p.querySelector('[data-retour-niveau1]');
    if (r) r.focus();
  }

  niveau1() {
    this.p2.forEach((p) => { p.classList.remove('est-ouvert'); p.hidden = true; });
    this.querySelectorAll('[data-ouvrir-niveau2]').forEach(
      (b) => b.setAttribute('aria-expanded', 'false'));
  }

  header() {
    if (!this.classList.contains('entete--transparent')) return;
    const poser = () => this.classList.toggle('est-pose', scrollY > 8);
    poser();
    addEventListener('scroll', poser, { passive: true });
  }

  disconnectedCallback() {
    if (this.horloge) clearInterval(this.horloge);
    if (this.surTouche) removeEventListener('keydown', this.surTouche);
  }
}
customElements.define('mvt-entete', MvtEntete);

class MvtCarrousel extends HTMLElement {
  connectedCallback() {
    if (this.hasAttribute('monte')) return;
    this.setAttribute('monte', '');
    this.piste = this.querySelector('[data-piste]');
    if (!this.piste) return;
    this.vues = [...this.piste.children];
    if (this.vues.length < 2) return;

    this.points = this.querySelector('[data-points]');
    this.avant = this.querySelector('[data-avant]');
    this.apres = this.querySelector('[data-apres]');

    if (this.points) {
      this.vues.forEach((_, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'carrousel__point';
        b.setAttribute('aria-label', `Aller à l'élément ${i + 1} sur ${this.vues.length}`);
        b.addEventListener('click', () => this.aller(i));
        this.points.appendChild(b);
      });
    }
    if (this.avant) this.avant.addEventListener('click', () => this.decaler(-1));
    if (this.apres) this.apres.addEventListener('click', () => this.decaler(1));

    this.piste.addEventListener('scroll', () => {
      clearTimeout(this.attente);
      this.attente = setTimeout(() => this.majEtat(), 90);
    }, { passive: true });
    this.majEtat();
    this.amorcer();
  }

  amorcer() {
    if (!('IntersectionObserver' in window)) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const io = new IntersectionObserver((entrees) => {
      if (!entrees.some(e => e.isIntersecting)) return;
      io.disconnect();
      if (this.piste.scrollWidth - this.piste.clientWidth < 24 || this.piste.scrollLeft > 2) return;
      setTimeout(() => {
        if (this.piste.scrollLeft > 2) return;

        const pi = this.piste;
        pi.style.scrollSnapType = 'none';
        pi.scrollTo({ left: 64, behavior: 'smooth' });
        setTimeout(() => {
          pi.scrollTo({ left: 0, behavior: 'smooth' });
          setTimeout(() => { pi.style.scrollSnapType = ''; }, 600);
        }, 700);
      }, 450);
    }, { threshold: 0.6 });
    io.observe(this.piste);
  }

  rang() {
    const g = this.piste.getBoundingClientRect().left;
    let meilleur = 0, ecart = Infinity;
    this.vues.forEach((v, i) => {
      const d = Math.abs(v.getBoundingClientRect().left - g);
      if (d < ecart) { ecart = d; meilleur = i; }
    });
    return meilleur;
  }

  aller(i) {
    const v = this.vues[Math.max(0, Math.min(this.vues.length - 1, i))];
    this.piste.scrollTo({ left: v.offsetLeft - this.piste.offsetLeft, behavior: 'smooth' });
  }

  decaler(n) { this.aller(this.rang() + n); }

  majEtat() {
    const i = this.rang();
    if (this.points) {
      [...this.points.children].forEach((b, k) => {
        b.classList.toggle('est-actif', k === i);
        b.setAttribute('aria-current', k === i ? 'true' : 'false');
      });
    }

    const fin = this.piste.scrollWidth - this.piste.clientWidth;
    if (this.avant) this.avant.disabled = this.piste.scrollLeft <= 2;
    if (this.apres) this.apres.disabled = this.piste.scrollLeft >= fin - 2;
  }
}
customElements.define('mvt-carrousel', MvtCarrousel);

class MvtMots extends HTMLElement {
  connectedCallback() {
    if (this.hasAttribute('monte')) return;
    this.setAttribute('monte', '');
    const mots = [...this.querySelectorAll('[data-mot]')];
    if (mots.length < 2) return;
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.setAttribute('empile', '');
    let i = 0;
    mots.forEach((m, k) => { m.style.opacity = k === 0 ? '1' : '0'; });
    this.horloge = setInterval(() => {
      mots[i].style.opacity = '0';
      i = (i + 1) % mots.length;
      mots[i].style.opacity = '1';
    }, 2500);
  }
  disconnectedCallback() { if (this.horloge) clearInterval(this.horloge); }
}
customElements.define('mvt-mots', MvtMots);

class MvtBandeau extends HTMLElement {
  connectedCallback() {
    if (this.hasAttribute('monte')) return;
    this.setAttribute('monte', '');

    if (this.ferme()) { this.hidden = true; return; }

    this.querySelector('[data-fermer-bandeau]')
        .addEventListener('click', () => this.fermer());

    this.observer = new MutationObserver(() => this.arbitrer());
    const barre = document.querySelector('[data-barre-achat], .barre-achat');
    const menu = document.querySelector('[data-menu]');
    [barre, menu].forEach((n) => {
      if (n) this.observer.observe(n, { attributes: true, attributeFilter: ['class', 'hidden'] });
    });
    this.barre = barre; this.menu = menu;
    addEventListener('scroll', () => this.arbitrer(), { passive: true });
    this.arbitrer();
  }

  arbitrer() {
    const barreVisible = this.barre && !this.barre.hidden
      && this.barre.getBoundingClientRect().height > 0
      && getComputedStyle(this.barre).visibility !== 'hidden';
    const menuOuvert = this.menu && !this.menu.hidden;
    this.classList.toggle('est-tu', Boolean(barreVisible || menuOuvert));
  }

  ferme() {
    try {
      const t = Number(localStorage.getItem('snb-bandeau-diag') || 0);
      return t > 0 && (Date.now() - t) < 7 * 24 * 3600 * 1000;
    } catch (e) { return false; }
  }

  fermer() {
    try { localStorage.setItem('snb-bandeau-diag', String(Date.now())); } catch (e) {  }
    this.hidden = true;
  }

  disconnectedCallback() { if (this.observer) this.observer.disconnect(); }
}
customElements.define('mvt-bandeau', MvtBandeau);

document.addEventListener('scroll', (e) => {
  const piste = e.target;
  if (!piste || !piste.matches || !piste.matches('[data-carte-vues]')) return;
  const i = Math.round(piste.scrollLeft / Math.max(1, piste.clientWidth));
  const points = piste.parentElement.querySelectorAll('.carte__points i');
  points.forEach((p, k) => p.classList.toggle('est-actif', k === i));
}, { capture: true, passive: true });

class MvtPeau extends HTMLElement {
  connectedCallback() {
    if (this.hasAttribute('monte')) return;
    this.setAttribute('monte', '');
    const section = this.closest('section') || document;
    this.plein = section.querySelector('[data-peau-plein]');
    this.video = this.plein ? this.plein.querySelector('video') : null;
    this.son = this.plein ? this.plein.querySelector('[data-peau-son]') : null;

    const auto = this.querySelector('video[data-peau-auto]');
    if (auto && !DOUX.matches && 'IntersectionObserver' in window) {
      new IntersectionObserver(([e]) => {
        if (e.isIntersecting) { auto.preload = 'auto'; const p = auto.play(); if (p && p.catch) p.catch(() => {}); }
        else auto.pause();
      }, { threshold: 0.4 }).observe(auto);
    }

    this.querySelectorAll('[data-peau-video]').forEach((b) => {
      b.addEventListener('click', () => this.ouvrir(b));
    });
    if (!this.plein) return;
    this.plein.querySelector('[data-peau-fermer]')?.addEventListener('click', () => this.fermer());
    this.plein.addEventListener('click', (e) => { if (e.target === this.plein) this.fermer(); });
    this.plein.addEventListener('close', () => { this.video?.pause(); });
    this.son?.addEventListener('click', () => this.basculerSon());

    this.plein.querySelector('[data-peau-ajouter]')?.addEventListener('click', () => {
      setTimeout(() => this.fermer(), 0);
    });
  }

  ouvrir(bouton) {
    if (!this.plein || !this.video) return;
    this.querySelector('video[data-peau-auto]')?.pause();
    this.video.muted = true;
    this.video.poster = bouton.dataset.peauAffiche || '';
    this.video.src = bouton.dataset.peauVideo;
    this.majSon();
    if (typeof this.plein.showModal === 'function') this.plein.showModal();
    else this.plein.setAttribute('open', '');
    const p = this.video.play(); if (p && p.catch) p.catch(() => {});
    this.dernier = bouton;
  }

  fermer() {
    if (!this.plein) return;
    this.video?.pause();
    if (typeof this.plein.close === 'function' && this.plein.open) this.plein.close();
    else this.plein.removeAttribute('open');
    this.dernier?.focus({ preventScroll: true });
  }

  basculerSon() {
    if (!this.video) return;
    this.video.muted = !this.video.muted;
    if (!this.video.muted) { const p = this.video.play(); if (p && p.catch) p.catch(() => {}); }
    this.majSon();
  }

  majSon() {
    if (!this.son) return;
    const texte = this.son.querySelector('[data-peau-son-texte]');
    if (texte) texte.textContent = this.video.muted ? 'Activer le son' : 'Couper le son';
    this.son.setAttribute('aria-pressed', String(!this.video.muted));
    const icone = this.son.querySelector('[data-peau-son-icone]');
    if (icone) icone.innerHTML = this.video.muted
      ? '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" width="18" height="18" aria-hidden="true"><path d="M4 9.5h3.5L12 5.5v13l-4.5-4H4Z"/><path d="M16 9.5l5 5M21 9.5l-5 5"/></svg>'
      : '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" width="18" height="18" aria-hidden="true"><path d="M4 9.5h3.5L12 5.5v13l-4.5-4H4Z"/><path d="M15.5 9a4.2 4.2 0 0 1 0 6M18 6.5a7.8 7.8 0 0 1 0 11"/></svg>';
  }
}
customElements.define('mvt-peau', MvtPeau);
